import { User, Star, Settings, Bell, RefreshCw, Info, Moon, Sun, ChevronRight } from 'lucide-react';
import { APP_NAME, APP_SLOGAN } from '../../config';

interface ProfileProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

export default function Profile({ isDarkMode, onToggleTheme }: ProfileProps) {
  const menuItems = [
    {
      id: 'favorites',
      label: '我的收藏',
      icon: Star,
      badge: '12'
    },
    {
      id: 'notifications',
      label: '通知设置',
      icon: Bell
    },
    {
      id: 'refresh',
      label: '数据刷新频率',
      icon: RefreshCw,
      value: '5分钟'
    },
    {
      id: 'theme',
      label: '主题模式',
      icon: isDarkMode ? Moon : Sun,
      value: isDarkMode ? '暗色' : '亮色',
      action: onToggleTheme
    }
  ];

  const aboutItems = [
    {
      id: 'version',
      label: '版本信息',
      icon: Info,
      value: 'v1.0.0'
    },
    {
      id: 'settings',
      label: '系统设置',
      icon: Settings
    }
  ];

  const MenuItem = ({ item, showBorder = true }: { item: typeof menuItems[0], showBorder?: boolean }) => {
    const Icon = item.icon;
    
    return (
      <button
        onClick={item.action}
        className={`w-full flex items-center justify-between px-4 py-4 hover:bg-muted/30 transition-colors ${
          showBorder ? 'border-b border-border' : ''
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#1a56db]/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#1a56db]" />
          </div>
          <span>{item.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {item.badge && (
            <span className="px-2 py-0.5 bg-[#dc3545] text-white text-xs rounded-full">
              {item.badge}
            </span>
          )}
          {item.value && (
            <span className="text-sm text-muted-foreground">{item.value}</span>
          )}
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1a56db] to-[#2563eb] px-4 py-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1 text-white">
            <h2 className="mb-1">投资者</h2>
            <p className="text-sm opacity-90">欢迎使用 {APP_NAME}</p>
          </div>
        </div>
      </div>

      {/* Menu Sections */}
      <div className="flex-1 px-4 py-4 space-y-4">
        {/* Main Menu */}
        <div className="bg-card rounded-xl overflow-hidden shadow-sm border border-border">
          {menuItems.map((item, index) => (
            <MenuItem 
              key={item.id} 
              item={item} 
              showBorder={index < menuItems.length - 1}
            />
          ))}
        </div>

        {/* About Section */}
        <div className="bg-card rounded-xl overflow-hidden shadow-sm border border-border">
          {aboutItems.map((item, index) => (
            <MenuItem 
              key={item.id} 
              item={item} 
              showBorder={index < aboutItems.length - 1}
            />
          ))}
        </div>

        {/* App Info */}
        <div className="text-center py-6">
          <div className="inline-flex items-center justify-center gap-2 mb-2">
            <img src="/logo.svg" alt={APP_NAME} className="w-8 h-8 drop-shadow-md" />
            <span className="font-semibold">{APP_NAME}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {APP_SLOGAN}
          </p>
        </div>
      </div>
    </div>
  );
}
